/**
 * Kongsi — the one share card every Share button opens, and the channel it
 * went out on. Shared between the card (which builds each app's link), the
 * server (which counts the taps and credits the sales) and the dashboard.
 *
 * A shared link carries `sh=<channel>`. The buyer who opens it keeps that
 * channel for SHARE_TOUCH_TTL_MS, and an order placed in that time is credited
 * to it — the same idea as `fyp=<code>`, kept apart from the ad click
 * (utm_*) so a WhatsApp share never takes an ad's credit or the other way round.
 */

export const SHARE_PARAM = "sh";

/** Order is the card's: the apps people send links on first. */
export const SHARE_CHANNELS = ["wa", "fb", "ms", "tg", "th", "x", "em", "sms", "cp", "nv"] as const;
export type ShareChannel = (typeof SHARE_CHANNELS)[number];

export const SHARE_CHANNEL_LABEL: Record<ShareChannel, string> = {
  wa: "WhatsApp",
  fb: "Facebook",
  ms: "Messenger",
  tg: "Telegram",
  th: "Threads",
  x: "X",
  em: "Emel",
  sms: "SMS",
  cp: "Salin pautan",
  nv: "Kongsi telefon",
};

/** Apps that only open from a phone: Messenger's share link needs its app, and a desktop has no SMS. */
export const PHONE_ONLY_CHANNELS: readonly ShareChannel[] = ["ms", "sms"];

/** Where the Share button was. `ref` names the thing on it (see shareRefOk). */
export const SHARE_SURFACES = ["fyp", "profil", "affiliate", "halaman", "komuniti", "bio", "produk"] as const;
export type ShareSurface = (typeof SHARE_SURFACES)[number];

export const SHARE_SURFACE_LABEL: Record<ShareSurface, string> = {
  fyp: "Video FYP",
  profil: "Profil FYP",
  affiliate: "Pautan affiliate",
  halaman: "Halaman jualan (affiliate)",
  komuniti: "Posting komuniti",
  bio: "Bio-web",
  produk: "Pautan produk",
};

/** A buyer usually buys on the day; a week covers the one who comes back. */
export const SHARE_TOUCH_TTL_MS = 7 * 86_400_000;

export function isShareChannel(v: unknown): v is ShareChannel {
  return typeof v === "string" && (SHARE_CHANNELS as readonly string[]).includes(v);
}

export function isShareSurface(v: unknown): v is ShareSurface {
  return typeof v === "string" && (SHARE_SURFACES as readonly string[]).includes(v);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What `ref` must look like for each surface: a video code, a feed slug, or an id. */
export function shareRefOk(surface: ShareSurface, ref: string): boolean {
  if (surface === "fyp") return /^[a-z0-9]{7}$/.test(ref);
  if (surface === "profil") return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(ref) && ref.length <= 64;
  return UUID.test(ref);
}

/** The link as it goes out on `channel`. A link that will not parse goes out as it is. */
export function taggedShareUrl(url: string, channel: ShareChannel): string {
  try {
    const u = new URL(url);
    u.searchParams.set(SHARE_PARAM, channel);
    return u.toString();
  } catch {
    return url;
  }
}

/** The same link through an affiliate's referral handler, `/<path>/r/<CODE>`, which records the
 *  click and lands the buyer on the page as it was (query and all). */
export function referralUrl(url: string, code: string): string {
  try {
    const u = new URL(url);
    u.pathname = `${u.pathname.replace(/\/+$/, "")}/r/${encodeURIComponent(code)}`;
    return u.toString();
  } catch {
    return url;
  }
}

/** The channel a landing address carries, or null. */
export function shareChannelFromSearch(search: string): ShareChannel | null {
  try {
    const v = new URLSearchParams(search).get(SHARE_PARAM);
    return isShareChannel(v) ? v : null;
  } catch {
    return null;
  }
}

export type StoredShareTouch = { ch: ShareChannel; at: number };

/** A kept channel, if it is still inside the window. */
export function liveShareTouch(stored: string | null, now: number): ShareChannel | null {
  if (!stored) return null;
  try {
    const t = JSON.parse(stored) as Partial<StoredShareTouch>;
    if (!isShareChannel(t.ch) || typeof t.at !== "number") return null;
    if (now < t.at || now - t.at > SHARE_TOUCH_TTL_MS) return null;
    return t.ch;
  } catch {
    return null;
  }
}

export interface ShareContent {
  /** Already tagged for the channel (taggedShareUrl). */
  url: string;
  /** The headline: a product or store name. Email uses it as the subject. */
  title: string;
  /** A line sent ahead of the link, where the app takes one. */
  text?: string;
}

const enc = encodeURIComponent;

/** Text first, link on its own line — how a link reads in a chat. */
export function shareMessage(text: string | undefined, url: string): string {
  const t = text?.trim();
  return t ? `${t}\n${url}` : url;
}

/**
 * The address that opens `channel` with the link filled in, or null for the two
 * that are not addresses (copy, the phone's own sheet). Facebook takes the link
 * alone and shows the page's own preview; the others take a line of text too.
 */
export function shareTargetHref(channel: ShareChannel, c: ShareContent): string | null {
  const msg = shareMessage(c.text, c.url);
  switch (channel) {
    case "wa":
      return `https://wa.me/?text=${enc(msg)}`;
    case "fb":
      return `https://www.facebook.com/sharer/sharer.php?u=${enc(c.url)}`;
    case "ms":
      return `fb-messenger://share/?link=${enc(c.url)}`;
    case "tg":
      return c.text?.trim()
        ? `https://t.me/share/url?url=${enc(c.url)}&text=${enc(c.text.trim())}`
        : `https://t.me/share/url?url=${enc(c.url)}`;
    case "th":
      return `https://www.threads.net/intent/post?text=${enc(msg)}`;
    case "x":
      return c.text?.trim()
        ? `https://x.com/intent/post?text=${enc(c.text.trim())}&url=${enc(c.url)}`
        : `https://x.com/intent/post?url=${enc(c.url)}`;
    case "em":
      return `mailto:?subject=${enc(c.title)}&body=${enc(msg)}`;
    case "sms":
      // `sms:?&body=` is the one spelling both iOS and Android read.
      return `sms:?&body=${enc(msg)}`;
    case "cp":
    case "nv":
      return null;
  }
}

/** Opens in a new tab: a web address. The app schemes (mailto:, sms:, fb-messenger:) open in place. */
export function opensInNewTab(href: string): boolean {
  return /^https:\/\//.test(href);
}

/** One row of the dashboard's channel table. */
export interface ShareChannelBreakdown {
  channel: ShareChannel;
  label: string;
  /** Taps on this channel in the share card. */
  shares: number;
  /** Paid orders from a link shared on this channel. */
  orders: number;
  revenue: number;
}

/** Channels with any taps or sales, best seller first, then most shared. */
export function rankShareChannels(
  shares: ReadonlyMap<ShareChannel, number>,
  sales: ReadonlyMap<ShareChannel, { orders: number; revenue: number }>,
): ShareChannelBreakdown[] {
  return SHARE_CHANNELS.map((channel) => ({
    channel,
    label: SHARE_CHANNEL_LABEL[channel],
    shares: shares.get(channel) ?? 0,
    orders: sales.get(channel)?.orders ?? 0,
    revenue: Math.round((sales.get(channel)?.revenue ?? 0) * 100) / 100,
  }))
    .filter((r) => r.shares > 0 || r.orders > 0)
    .sort((a, b) => b.revenue - a.revenue || b.orders - a.orders || b.shares - a.shares);
}
